import * as React from 'react';
import {Query, Mutation} from 'react-apollo';

import {stripTypename} from 'nutshell-core/utils/strip-typename';

import type {
    GetClosedLeadTaskTemplate as GetClosedLeadTaskTemplateQuery,
    GetClosedLeadTaskTemplateVariables as GetClosedLeadTaskTemplateQueryVariables,
    CreateClosedLeadTaskTemplate as CreateClosedLeadTaskTemplateMutation,
    CreateClosedLeadTaskTemplateVariables as CreateClosedLeadTaskTemplateMutationVariables,
    UpdateClosedLeadTaskTemplate as UpdateClosedLeadTaskTemplateMutation,
    UpdateClosedLeadTaskTemplateVariables as UpdateClosedLeadTaskTemplateMutationVariables,
    DeleteClosedLeadTaskTemplate as DeleteClosedLeadTaskTemplateMutation,
    DeleteClosedLeadTaskTemplateVariables as DeleteClosedLeadTaskTemplateMutationVariables,
    CreateClosedLeadTaskTemplateInputFieldInput,
    UpdateClosedLeadTaskTemplateInputFieldInput,
    DeleteClosedLeadTaskTemplateInputFieldInput,
} from 'nutshell-graphql-types';

import type {ClosedLeadTaskTemplateItem} from '../../../../sales-automation/types';

import {ClosedLeadTaskTemplateForm} from '../../forms/closed-lead-task-template-form';
import type {TaskTemplateInput} from '../../forms/closed-lead-task-template-form';
import CREATE_CLOSED_LEAD_TASK_TEMPLATE from '../../graphql/mutations/create-closed-lead-task-template.graphql';
import DELETE_CLOSED_LEAD_TASK_TEMPLATE from '../../graphql/mutations/delete-closed-lead-task-template.graphql';
import UPDATE_CLOSED_LEAD_TASK_TEMPLATE from '../../graphql/mutations/update-closed-lead-task-template.graphql';
import GET_CLOSED_LEAD_TASK_TEMPLATE from '../../graphql/queries/get-closed-lead-task-template.graphql';

import {SalesAutomationEditorSidebar} from './sidebar/sales-automation-editor-sidebar';

type OwnProps = {
    isNew: boolean;
    stagesetId: string;
    parentId: string;
    entityId: string | null | undefined;
};

type GraphQLProps = {
    taskTemplate: ClosedLeadTaskTemplateItem | null | undefined;
    createTaskTemplate: (arg1: CreateClosedLeadTaskTemplateInputFieldInput) => Promise<any>;
    updateTaskTemplate: (arg1: UpdateClosedLeadTaskTemplateInputFieldInput) => Promise<any>;
    deleteTaskTemplate: (arg1: DeleteClosedLeadTaskTemplateInputFieldInput) => Promise<any>;
};

type Props = OwnProps & GraphQLProps;

class TaskTemplateEditorComponent extends React.PureComponent<Props> {
    sidebarFormSubmitBtnRef: HTMLInputElement | null | undefined;

    render() {
        if (this.props.entityId && !this.props.taskTemplate) return null;

        return (
            <SalesAutomationEditorSidebar
                onSave={this.handleSidebarFormSave}
                title={this.props.isNew ? 'New task' : 'Edit task'}
            >
                <ClosedLeadTaskTemplateForm
                    entityId={this.props.entityId}
                    taskTemplate={this.props.taskTemplate}
                    // @ts-expect-error
                    formSubmitBtnRef={(node) => {
                        this.sidebarFormSubmitBtnRef = node;
                    }}
                    onSubmit={this.handleSubmit}
                    onDelete={this.handleDelete}
                />
            </SalesAutomationEditorSidebar>
        );
    }

    handleSidebarFormSave = () => {
        if (this.sidebarFormSubmitBtnRef) {
            this.sidebarFormSubmitBtnRef.click();
        }
    };

    handleSubmit = (formData: TaskTemplateInput) => {
        // This is a refinement for flow, to guarantee that the parentId is an
        // expected outcomeType
        if (
            this.props.parentId !== 'LEAD_CANCELLED' &&
            this.props.parentId !== 'LEAD_WON' &&
            this.props.parentId !== 'LEAD_LOST'
        ) {
            return null;
        }

        const saveTaskTemplate = this.props.entityId
            ? this.props.updateTaskTemplate({
                  id: this.props.entityId,
                  name: formData.name,
                  description: formData.description,
                  dueDate:
                      formData.dueDate && formData.dueDate.dueTimespan
                          ? stripTypename(formData.dueDate)
                          : null,
                  assigneeId: formData.assigneeId,
              })
            : this.props.createTaskTemplate({
                  stagesetId: this.props.stagesetId,
                  outcomeType: this.props.parentId,
                  name: formData.name,
                  description: formData.description,
                  dueDate: formData.dueDate,
                  assigneeId: formData.assigneeId,
              });

        return saveTaskTemplate;
    };

    handleDelete = (taskTemplate: {id: string}) => {
        return this.props.deleteTaskTemplate(taskTemplate);
    };
}

class TaskQuery extends Query<
    GetClosedLeadTaskTemplateQuery,
    GetClosedLeadTaskTemplateQueryVariables
> {}

class CreateMutation extends Mutation<
    CreateClosedLeadTaskTemplateMutation,
    CreateClosedLeadTaskTemplateMutationVariables
> {}

class UpdateMutation extends Mutation<
    UpdateClosedLeadTaskTemplateMutation,
    UpdateClosedLeadTaskTemplateMutationVariables
> {}

class DeleteMutation extends Mutation<
    DeleteClosedLeadTaskTemplateMutation,
    DeleteClosedLeadTaskTemplateMutationVariables
> {}

export const TaskTemplateEditor = (props: OwnProps) => (
    <TaskQuery
        query={GET_CLOSED_LEAD_TASK_TEMPLATE}
        skip={!props.entityId}
        variables={{taskTemplateId: props.entityId || ''}}
    >
        {({data}) => (
            <CreateMutation mutation={CREATE_CLOSED_LEAD_TASK_TEMPLATE}>
                {(createTask) => (
                    <UpdateMutation mutation={UPDATE_CLOSED_LEAD_TASK_TEMPLATE}>
                        {(updateTask) => (
                            <DeleteMutation mutation={DELETE_CLOSED_LEAD_TASK_TEMPLATE}>
                                {(deleteTask) => (
                                    <TaskTemplateEditorComponent
                                        {...props}
                                        taskTemplate={data ? data.closedLeadTaskTemplate : null}
                                        createTaskTemplate={(taskTemplate) =>
                                            createTask({
                                                variables: {
                                                    taskTemplate,
                                                },
                                            })
                                        }
                                        updateTaskTemplate={(taskTemplate) =>
                                            updateTask({
                                                variables: {
                                                    taskTemplate,
                                                },
                                            })
                                        }
                                        deleteTaskTemplate={(taskTemplate) =>
                                            deleteTask({
                                                variables: {
                                                    taskTemplate,
                                                },
                                            })
                                        }
                                    />
                                )}
                            </DeleteMutation>
                        )}
                    </UpdateMutation>
                )}
            </CreateMutation>
        )}
    </TaskQuery>
);
